import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useOpenFinanceConfig, useUpdateOpenFinanceConfig } from '../../../hooks/useOpenFinance';

export function OpenFinanceConfig() {
  const { data: config, isLoading } = useOpenFinanceConfig();
  const updateConfig = useUpdateOpenFinanceConfig();
  
  const [formData, setFormData] = useState({
    participantId: '',
    organizationId: '',
    webhookUrl: '',
    maxConsentDurationDays: 365,
    rateLimitPerMinute: 100,
    isEnabled: false,
  });

  const [isEditing, setIsEditing] = useState(false);

  const handleEdit = () => {
    if (config) {
      setFormData({
        participantId: config.participantId || '',
        organizationId: config.organizationId || '',
        webhookUrl: config.webhookUrl || '',
        maxConsentDurationDays: config.maxConsentDurationDays,
        rateLimitPerMinute: config.rateLimitPerMinute,
        isEnabled: config.isEnabled,
      });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    await updateConfig.mutateAsync(formData);
    setIsEditing(false);
  };

  const handleToggleModule = async () => {
    await updateConfig.mutateAsync({ isEnabled: !config?.isEnabled });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuração Open Finance</h1>
          <p className="text-gray-500">Configure a integração com Open Banking Brasil</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant={config?.isEnabled ? 'success' : 'default'}>
            {config?.isEnabled ? 'Ativo' : 'Inativo'}
          </Badge>
          <Button
            variant={config?.isEnabled ? 'secondary' : 'primary'}
            onClick={handleToggleModule}
            disabled={updateConfig.isPending}
          >
            {config?.isEnabled ? 'Desativar Módulo' : 'Ativar Módulo'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Identificação do Participante</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">ID do Participante</label>
                  <Input
                    value={formData.participantId}
                    onChange={(e) => setFormData({ ...formData, participantId: e.target.value })}
                    placeholder="Ex: 12345678-0001-00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ID da Organização</label>
                  <Input
                    value={formData.organizationId}
                    onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                    placeholder="Ex: ORG-12345"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-sm text-gray-500">ID do Participante</span>
                  <p className="font-medium">{config?.participantId || 'Não configurado'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">ID da Organização</span>
                  <p className="font-medium">{config?.organizationId || 'Não configurado'}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Webhooks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <div>
                <label className="block text-sm font-medium mb-1">URL do Webhook</label>
                <Input
                  value={formData.webhookUrl}
                  onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                  placeholder="https://api.exemplo.com/webhooks/open-finance"
                />
              </div>
            ) : (
              <div>
                <span className="text-sm text-gray-500">URL do Webhook</span>
                <p className="font-medium break-all">{config?.webhookUrl || 'Não configurado'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Limites</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Duração Máxima do Consentimento (dias)</label>
                  <Input
                    type="number"
                    value={formData.maxConsentDurationDays}
                    onChange={(e) => setFormData({ ...formData, maxConsentDurationDays: parseInt(e.target.value) })}
                    min={1}
                    max={365}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Rate Limit (requisições/minuto)</label>
                  <Input
                    type="number"
                    value={formData.rateLimitPerMinute}
                    onChange={(e) => setFormData({ ...formData, rateLimitPerMinute: parseInt(e.target.value) })}
                    min={1}
                    max={1000}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-sm text-gray-500">Duração Máxima do Consentimento</span>
                  <p className="font-medium">{config?.maxConsentDurationDays} dias</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Rate Limit</span>
                  <p className="font-medium">{config?.rateLimitPerMinute} req/min</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Certificados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <span className="text-sm text-gray-500">Certificado de Assinatura</span>
              <p className="font-medium">
                {config?.signingCertificate ? (
                  <Badge variant="success">Configurado</Badge>
                ) : (
                  <Badge variant="default">Não configurado</Badge>
                )}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Certificado de Transporte</span>
              <p className="font-medium">
                {config?.transportCertificate ? (
                  <Badge variant="success">Configurado</Badge>
                ) : (
                  <Badge variant="default">Não configurado</Badge>
                )}
              </p>
            </div>
            <p className="text-xs text-gray-400">
              Os certificados devem ser configurados via API ou console de administração.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-4">
        {isEditing ? (
          <>
            <Button variant="secondary" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateConfig.isPending}>
              {updateConfig.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        ) : (
          <Button onClick={handleEdit}>Editar Configurações</Button>
        )}
      </div>
    </div>
  );
}

export default OpenFinanceConfig;
